<template>
  <article class="w-full">
    <Head :title="technique.title">
      <meta
        head-key="description"
        name="description"
        :content="technique.intro || `Läs om ${technique.title}.`"
      />
    </Head>
    <!-- Hero -->
    <div
      class="relative w-full overflow-hidden"
      style="height: clamp(280px, 45vw, 520px)"
    >
      <img
        v-if="technique.heroImageUrl"
        :src="technique.heroImageUrl"
        :alt="technique.title"
        class="w-full h-full object-cover"
        style="object-position: center 40%"
      />
      <div
        v-else
        class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600"
      />

      <!-- Gradient overlay -->
      <div
        class="absolute inset-0"
        style="
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(0, 0, 0, 0.35) 50%,
            rgba(0, 0, 0, 0.05) 100%
          );
        "
      />

      <!-- Back link -->
      <Link
        :href="toolsPath"
        class="absolute top-5 left-4 md:top-7 md:left-8 flex items-center gap-1.5 hover:opacity-80 transition-opacity no-underline"
      >
        <div
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style="
            background-color: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(8px);
            border: 1px solid rgba(255, 255, 255, 0.2);
          "
        >
          <ArrowLeft :size="13" color="white" />
          <span
            class="font-[Poppins] font-bold text-[12px] text-white tracking-[0.3px]"
            >Tekniker &amp; verktyg</span
          >
        </div>
      </Link>

      <!-- Title overlay -->
      <div class="absolute bottom-0 left-0 right-0 pb-7 md:pb-10">
        <div class="max-w-[1280px] mx-auto px-4 md:px-8">
          <span
            class="inline-block px-3 py-1 rounded-full mb-3 text-[10px] font-bold tracking-[1px]"
            :style="{ backgroundColor: technique.badgeColor, color: '#fff' }"
            >{{ technique.badge }}</span
          >
          <h1
            class="font-[Poppins] text-white m-0 mb-3 leading-[1.15] tracking-[-1px] max-w-[760px]"
            style="font-size: clamp(26px, 5vw, 52px)"
          >
            {{ technique.title }}
          </h1>
          <div class="flex items-center gap-2 text-white/70 text-[13px]">
            <Clock :size="13" />
            <span>{{ technique.readTime }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Body + sidebar -->
    <div class="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
      <div class="flex gap-10 lg:gap-16 items-start">
        <!-- Main article content -->
        <div class="flex-1 min-w-0 max-w-[700px]">
          <!-- Intro paragraph -->
          <p
            v-if="technique.intro"
            class="text-[#3c4947] leading-[1.85] m-0 mb-8"
            style="font-size: clamp(16px, 2vw, 18px)"
          >
            {{ technique.intro }}
          </p>

          <!-- Body blocks -->
          <div class="flex flex-col gap-6">
            <template v-for="(block, idx) in technique.bodyBlocks" :key="idx">
              <!-- text -->
              <p
                v-if="block.type === 'text'"
                class="text-[#3c4947] leading-[1.85] m-0"
                style="font-size: clamp(16px, 2vw, 18px)"
              >
                {{ block.content }}
              </p>

              <!-- heading -->
              <h2
                v-else-if="block.type === 'heading'"
                class="font-[Poppins] font-bold text-[#191c1e] m-0 leading-[1.3]"
                style="font-size: clamp(20px, 2.5vw, 24px)"
              >
                {{ block.content }}
              </h2>

              <!-- tip -->
              <div
                v-else-if="block.type === 'tip'"
                class="flex gap-4 rounded-2xl p-5 md:p-6"
                style="
                  background-color: rgba(20, 184, 166, 0.06);
                  border: 1px solid rgba(20, 184, 166, 0.18);
                "
              >
                <Lightbulb :size="20" class="text-teal-500 shrink-0 mt-0.5" />
                <div class="flex flex-col gap-1.5">
                  <p
                    class="font-[Poppins] font-bold text-[14px] text-teal-500 m-0"
                  >
                    {{ block.title }}
                  </p>
                  <p class="text-[15px] text-[#3c4947] leading-[1.75] m-0">
                    {{ block.content }}
                  </p>
                </div>
              </div>

              <!-- warning -->
              <div
                v-else-if="block.type === 'warning'"
                class="flex gap-4 rounded-2xl p-5 md:p-6"
                style="
                  background-color: rgba(245, 158, 11, 0.06);
                  border: 1px solid rgba(245, 158, 11, 0.25);
                "
              >
                <AlertTriangle
                  :size="20"
                  class="text-amber-500 shrink-0 mt-0.5"
                />
                <div class="flex flex-col gap-1.5">
                  <p
                    class="font-[Poppins] font-bold text-[14px] text-amber-700 m-0"
                  >
                    {{ block.title }}
                  </p>
                  <p class="text-[15px] text-[#3c4947] leading-[1.75] m-0">
                    {{ block.content }}
                  </p>
                </div>
              </div>

              <!-- pullquote -->
              <div
                v-else-if="block.type === 'pullquote'"
                class="border-l-4 pl-6 py-1 my-2"
                style="border-color: #14b8a6"
              >
                <p
                  class="italic font-light text-[#191c1e] leading-[1.6] m-0"
                  style="font-size: clamp(18px, 2.5vw, 22px)"
                >
                  {{ block.content }}
                </p>
              </div>

              <!-- keypoints -->
              <div
                v-else-if="block.type === 'keypoints'"
                class="rounded-2xl md:rounded-3xl p-6 md:p-8"
                style="
                  background-color: #f8fffe;
                  border: 2px solid rgba(20, 184, 166, 0.15);
                "
              >
                <div class="flex items-center gap-2 mb-4">
                  <CheckCircle2 :size="18" class="text-teal-500" />
                  <p
                    class="font-[Poppins] font-bold text-[13px] text-teal-500 tracking-[1px] uppercase m-0"
                  >
                    {{ block.label }}
                  </p>
                </div>
                <ul class="flex flex-col gap-3 m-0 p-0 list-none">
                  <li
                    v-for="(item, i) in block.items"
                    :key="i"
                    class="flex items-start gap-3"
                  >
                    <span
                      class="shrink-0 rounded-full bg-teal-500"
                      style="width: 7px; height: 7px; margin-top: 8px"
                    />
                    <span class="text-[15px] text-[#3c4947] leading-[1.65]">{{
                      item
                    }}</span>
                  </li>
                </ul>
              </div>

              <!-- image -->
              <figure v-else-if="block.type === 'image'" class="m-0">
                <div
                  class="overflow-hidden rounded-2xl md:rounded-3xl"
                  style="height: clamp(200px, 40vw, 380px)"
                >
                  <img
                    :src="block.src"
                    :alt="block.caption"
                    class="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <figcaption
                  v-if="block.caption"
                  class="text-[13px] text-[#6c7a77] italic mt-2.5 leading-[1.6]"
                >
                  {{ block.caption }}
                </figcaption>
              </figure>
            </template>
          </div>
        </div>

        <!-- Sidebar (desktop only) -->
        <div
          class="hidden lg:flex flex-col gap-5 w-[260px] shrink-0 sticky top-24"
        >
          <!-- Takeaways -->
          <div
            v-if="technique.takeaways?.length"
            class="rounded-2xl p-5 bg-teal-50/60 border border-teal-100"
          >
            <div class="flex items-center gap-2 mb-3">
              <CheckCircle2 :size="16" class="text-teal-500" />
              <p
                class="font-[Poppins] font-bold text-[11px] text-teal-500 tracking-[1.2px] uppercase m-0"
              >
                Kom ihåg
              </p>
            </div>
            <ul class="flex flex-col gap-2.5 m-0 p-0 list-none">
              <li
                v-for="(item, i) in technique.takeaways"
                :key="i"
                class="flex items-start gap-2.5"
              >
                <span
                  class="shrink-0 rounded-full bg-teal-500"
                  style="width: 6px; height: 6px; margin-top: 7px"
                />
                <span class="text-[13px] text-[#3c4947] leading-[1.6]">{{
                  item
                }}</span>
              </li>
            </ul>
          </div>

          <!-- Badge/category -->
          <div class="rounded-2xl p-5 bg-[#f8fffe]">
            <p
              class="font-[Poppins] font-bold text-[11px] text-[#6c7a77] tracking-[1.2px] uppercase m-0 mb-2"
            >
              Kategori
            </p>
            <span
              class="inline-block px-3 py-1 rounded-full text-[12px] font-bold font-[Poppins] text-white"
              :style="{ backgroundColor: technique.badgeColor }"
              >{{ technique.badge }}</span
            >
          </div>

          <!-- Related (desktop) -->
          <div
            v-if="related.length || linkedTools.length"
            class="flex flex-col gap-3"
          >
            <p
              class="font-[Poppins] font-bold text-[11px] text-[#6c7a77] tracking-[1.2px] uppercase m-0 mb-1"
            >
              Relaterade tekniker och verktyg
            </p>
            <Link
              v-for="rel in related"
              :key="rel.id"
              :href="rel.path"
              class="flex gap-3 items-center hover:opacity-80 transition-opacity no-underline"
            >
              <div
                class="overflow-hidden rounded-xl shrink-0"
                style="width: 56px; height: 56px"
              >
                <img
                  v-if="rel.heroImageUrl"
                  :src="rel.heroImageUrl"
                  :alt="rel.title"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
                <div v-else class="w-full h-full bg-teal-100 rounded-xl" />
              </div>
              <div>
                <p
                  class="font-[Poppins] font-bold text-[13px] text-[#191c1e] m-0 mb-0.5 leading-[1.3]"
                >
                  {{ rel.title }}
                </p>
                <p class="text-[12px] text-[#6c7a77] m-0">{{ rel.readTime }}</p>
              </div>
            </Link>
            <a
              v-for="tool in linkedTools"
              :key="tool.path"
              :href="tool.path"
              class="flex gap-3 items-center hover:opacity-80 transition-opacity no-underline"
            >
              <div
                class="flex items-center justify-center overflow-hidden rounded-xl shrink-0 bg-teal-50 border border-teal-100"
                style="width: 56px; height: 56px"
              >
                <Calculator :size="22" class="text-teal-500" />
              </div>
              <div>
                <p
                  class="font-[Poppins] font-bold text-[13px] text-[#191c1e] m-0 mb-0.5 leading-[1.3]"
                >
                  {{ tool.label }}
                </p>
                <p class="text-[12px] text-[#6c7a77] m-0">Verktyg</p>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Related (mobile) -->
      <div
        v-if="related.length || linkedTools.length"
        class="lg:hidden mt-12 pt-8 border-t border-[#eceef0]"
      >
        <p
          class="font-[Poppins] font-bold text-[11px] text-[#6c7a77] tracking-[1.2px] uppercase m-0 mb-4"
        >
          Relaterade tekniker och verktyg
        </p>
        <div class="flex flex-col gap-4">
          <Link
            v-for="rel in related"
            :key="rel.id"
            :href="rel.path"
            class="flex gap-4 items-center hover:opacity-80 transition-opacity no-underline"
          >
            <div
              class="overflow-hidden rounded-xl shrink-0"
              style="width: 64px; height: 64px"
            >
              <img
                v-if="rel.heroImageUrl"
                :src="rel.heroImageUrl"
                :alt="rel.title"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <div v-else class="w-full h-full bg-teal-100 rounded-xl" />
            </div>
            <div class="flex-1">
              <p
                class="font-[Poppins] font-bold text-[14px] text-[#191c1e] m-0 mb-0.5"
              >
                {{ rel.title }}
              </p>
              <p class="text-[13px] text-[#6c7a77] m-0">{{ rel.readTime }}</p>
            </div>
            <ArrowRight :size="16" class="text-teal-500 shrink-0" />
          </Link>
          <a
            v-for="tool in linkedTools"
            :key="tool.path"
            :href="tool.path"
            class="flex gap-4 items-center hover:opacity-80 transition-opacity no-underline"
          >
            <div
              class="flex items-center justify-center overflow-hidden rounded-xl shrink-0 bg-teal-50 border border-teal-100"
              style="width: 64px; height: 64px"
            >
              <Calculator :size="26" class="text-teal-500" />
            </div>
            <div class="flex-1">
              <p
                class="font-[Poppins] font-bold text-[14px] text-[#191c1e] m-0 mb-0.5"
              >
                {{ tool.label }}
              </p>
              <p class="text-[13px] text-[#6c7a77] m-0">Verktyg</p>
            </div>
            <ArrowRight :size="16" class="text-teal-500 shrink-0" />
          </a>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup>
import { Head, Link } from "@inertiajs/vue3";
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  Clock,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
} from "lucide-vue-next";

defineProps({
  technique: { type: Object, required: true },
  related: { type: Array, default: () => [] },
  linkedTools: { type: Array, default: () => [] },
  toolsPath: { type: String, required: true },
});
</script>
